import { useDimensions } from "@repo/core/hooks/useDimensions";
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";

import { Text } from "@repo/core/components/core/Text";
import { convertDataRows } from "@repo/core/components/core/DataTable/utils";
import { TableConvertedData } from "@repo/core/components/core/DataTable/types";
import { exportTanstackTableToCSV } from "@repo/core/utils/csv.utils";
import { useMemo, useRef } from "react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { format } from "date-fns";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { ReportsResource } from "@repo/core/services/resources/reports";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { useQuery } from "@tanstack/react-query";
import { useReportsFilters } from "@repo/core/hooks/useReportsFilters";

const reportsResource = new ReportsResource(newApInstance);

export const ApprovalTypesController = () => {
  const { isMobile } = useDimensions();

  const { push } = useNavigation();
  const { isMobile: isMobileDevice } = useDeviceType();

  const {
    selectedAttendant,
    selectedProduct,
    selectedStore,
    filterStartDate,
    filterEndDate,
    onChangeStartDate,
    onChangeEndDate
  } = useReportsFilters();

  const filterModalRef = useRef<IModalRefProps>(null);

  const { data: approvalTypesResponse, isFetching: isFetchingApprovalTypes } =
    useQuery({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: [
        "approvalTypes",
        filterStartDate,
        filterEndDate,
        selectedStore,
        selectedProduct,
        selectedAttendant
      ],
      queryFn: () =>
        reportsResource.getApprovalTypesReports({
          startDate: format(filterStartDate, "yyyy-MM-dd"),
          endDate: format(filterEndDate, "yyyy-MM-dd"),
          store: selectedStore,
          product: selectedProduct,
          attendant: selectedAttendant
        })
    });
  const columnHelper = createColumnHelper();

  const headerColumns = approvalTypesResponse?.data?.table.headers.map(
    (header) => {
      return columnHelper.accessor(header.key, {
        header: () => (
          <Text
            fontSize="$xs"
            color={header?.styles?.text?.color || "$textLight700"}
            fontWeight={header?.styles?.text?.fontWeight || 600}
          >
            {header.label}
          </Text>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: header?.styles
      });
    }
  );

  const { tableData, chartBarData, chartLineData, cardsData, hasReportData } =
    useMemo(() => {
      const tableData = convertDataRows(
        approvalTypesResponse?.data?.table.headers,
        approvalTypesResponse?.data?.table.rows
      );

      const chartBarData = approvalTypesResponse?.data?.charts?.find(
        (chart) => chart.type === "bar"
      );

      const chartLineData = approvalTypesResponse?.data?.charts?.find(
        (chart) => chart.type === "line"
      );

      const cardsData = approvalTypesResponse?.data?.mobileTable;

      const hasReportData =
        Array.isArray(approvalTypesResponse?.data?.table.rows) &&
        approvalTypesResponse.data.table.rows.length > 0;

      return {
        tableData,
        chartBarData,
        chartLineData,
        cardsData,
        hasReportData
      };
    }, [approvalTypesResponse?.data]);

  const table = useReactTable({
    data: tableData,
    columns: headerColumns as AccessorKeyColumnDef<TableConvertedData>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel()
  });

  const topRightText = useMemo(() => {
    return `Atualizado em ${format(new Date(), "dd/MM")} às ${format(new Date(), "HH:mm:ss")}`;
  }, []);

  const handleExportTable = () => {
    exportTanstackTableToCSV(
      `Relatório_Tipos_de_Aprovação_${filterStartDate}_${filterEndDate}`,
      approvalTypesResponse?.data?.table.headers,
      table
    );
  };

  const handleSelectFilters = () => {
    if (isMobileDevice) {
      push(ROUTES.reportSelectFilters);
    } else {
      if (filterModalRef.current) {
        filterModalRef.current.open();
      }
    }
  };

  return {
    isMobile,
    onChangeStartDate,
    onChangeEndDate,
    table,
    handleExportTable,
    filterModalRef,
    topRightText,
    filterStartDate,
    filterEndDate,
    chartBarData,
    chartLineData,
    cardsData,
    hasReportData,
    handleSelectFilters,
    isFetchingApprovalTypes
  };
};
